import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Users, FileText, CreditCard, GraduationCap, TrendingUp,
  Clock, CheckCircle, AlertCircle, ArrowUpRight, Download, 
  BarChart3, PieChart, Activity
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { useAuth } from '../../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function StatCard({ icon: Icon, label, value, trend, color, link }) {
  return (
    <Link to={link || '#'} className="block">
      <div className="bg-[#111111] border border-[#2a2a2a] rounded-xl p-6 hover:border-[#3a3a3a] transition-colors">
        <div className="flex items-start justify-between">
          <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${color}`}>
            <Icon className="w-6 h-6" />
          </div>
          {trend && (
            <span className="flex items-center text-xs text-green-400">
              <TrendingUp className="w-3 h-3 mr-1" />
              {trend}
            </span>
          )}
        </div>
        <p className="mt-4 text-3xl font-manrope font-bold text-white">{value}</p>
        <p className="text-sm text-gray-400 mt-1">{label}</p>
      </div>
    </Link>
  );
}

function SimpleBarChart({ data, title }) {
  if (!data || data.length === 0) return null;
  const maxValue = Math.max(...data.map(d => d.value));
  
  return (
    <div className="bg-[#111111] border border-[#2a2a2a] rounded-xl p-6">
      <h3 className="font-semibold text-white mb-6">{title}</h3>
      <div className="space-y-3">
        {data.map((item, i) => (
          <div key={i} className="flex items-center gap-3">
            <span className="text-sm text-gray-400 w-32 truncate">{item.name}</span>
            <div className="flex-1 bg-[#1a1a1a] rounded-full h-6 overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-amber-500 to-amber-400 rounded-full flex items-center justify-end pr-2"
                style={{ width: `${maxValue > 0 ? (item.value / maxValue) * 100 : 0}%`, minWidth: item.value > 0 ? '30px' : '0' }}
              >
                <span className="text-xs font-medium text-black">{item.value}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SimplePieChart({ data, title }) {
  if (!data || data.length === 0) return null;
  const total = data.reduce((sum, d) => sum + d.value, 0);
  
  return (
    <div className="bg-[#111111] border border-[#2a2a2a] rounded-xl p-6">
      <h3 className="font-semibold text-white mb-6">{title}</h3>
      <div className="flex justify-center mb-6">
        <div className="relative w-40 h-40">
          <svg viewBox="0 0 100 100" className="transform -rotate-90">
            {data.reduce((acc, item, i) => {
              const percentage = total > 0 ? (item.value / total) * 100 : 0;
              const offset = acc.offset;
              acc.elements.push(
                <circle
                  key={i}
                  cx="50"
                  cy="50"
                  r="40"
                  fill="transparent"
                  stroke={item.color || '#f59e0b'}
                  strokeWidth="20"
                  strokeDasharray={`${percentage * 2.51} ${251.2 - percentage * 2.51}`}
                  strokeDashoffset={-offset * 2.51}
                  className="transition-all duration-500"
                />
              );
              acc.offset += percentage;
              return acc;
            }, { elements: [], offset: 0 }).elements}
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-2xl font-bold text-white">{total}</span>
          </div>
        </div>
      </div>
      <div className="space-y-2">
        {data.map((item, i) => (
          <div key={i} className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color || '#f59e0b' }}></div>
              <span className="text-gray-400">{item.name}</span>
            </div>
            <span className="text-white font-medium">{item.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function TrendChart({ data, title }) {
  if (!data || data.length === 0) return null;
  const maxValue = Math.max(...data.map(d => Math.max(d.nuevos || 0, d.membresías || 0)));
  
  return (
    <div className="bg-[#111111] border border-[#2a2a2a] rounded-xl p-6">
      <h3 className="font-semibold text-white mb-6">{title}</h3>
      <div className="h-48 flex items-end justify-between gap-2">
        {data.map((item, i) => (
          <div key={i} className="flex-1 flex flex-col items-center gap-1">
            <div className="w-full flex gap-1 items-end h-40">
              <div 
                className="flex-1 bg-amber-500 rounded-t"
                style={{ height: `${maxValue > 0 ? (item.nuevos / maxValue) * 100 : 0}%`, minHeight: item.nuevos > 0 ? '4px' : '0' }}
                title={`Nuevos: ${item.nuevos}`}
              ></div>
              <div 
                className="flex-1 bg-green-500 rounded-t"
                style={{ height: `${maxValue > 0 ? (item.membresías / maxValue) * 100 : 0}%`, minHeight: item.membresías > 0 ? '4px' : '0' }}
                title={`Membresías: ${item.membresías}`}
              ></div>
            </div>
            <span className="text-xs text-gray-500">{item.month}</span>
          </div>
        ))}
      </div>
      <div className="flex justify-center gap-6 mt-4">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded bg-amber-500"></div>
          <span className="text-xs text-gray-400">Nuevos Prof.</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded bg-green-500"></div>
          <span className="text-xs text-gray-400">Membresías</span>
        </div>
      </div>
    </div>
  );
}

export function AdminDashboard() {
  const { token } = useAuth();
  const [stats, setStats] = useState(null);
  const [advancedStats, setAdvancedStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  async function fetchData() {
    try {
      const [statsRes, advancedRes] = await Promise.all([
        fetch(`${API}/admin/stats`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API}/admin/stats/advanced`, { headers: { 'Authorization': `Bearer ${token}` } })
      ]);

      if (statsRes.ok) setStats(await statsRes.json());
      if (advancedRes.ok) setAdvancedStats(await advancedRes.json());
    } catch (error) {
      console.error('Error fetching admin data:', error);
    } finally {
      setIsLoading(false);
    }
  }

  async function exportReport(type) {
    setIsExporting(true);
    try {
      const endpoint = type === 'professionals' ? '/admin/export/professionals' 
                     : type === 'memberships' ? '/admin/export/memberships'
                     : type === 'excel' ? '/admin/export/excel-completo'
                     : '/admin/export/report';
      
      const response = await fetch(`${API}${endpoint}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = type === 'report' ? 'reporte_cni.pdf' 
                   : type === 'excel' ? 'reporte_completo_cni.xlsx'
                   : `${type}_cni.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
      }
    } catch (error) {
      console.error('Error exporting:', error);
    } finally {
      setIsExporting(false);
    }
  }

  if (isLoading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
      </div>
    );
  }

  const overview = advancedStats?.overview || {};
  const charts = advancedStats?.charts || {};

  return (
    <div className="p-8" data-testid="admin-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-manrope font-bold text-2xl text-white mb-2">Panel de Administración</h1>
          <p className="text-gray-400">Estadísticas y gestión del Colegio Nacional de Inmobiliarios</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Link to="/admin/analytics">
            <Button className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-black font-semibold">
              <BarChart3 className="w-4 h-4 mr-2" />Analytics
            </Button>
          </Link>
          <Button onClick={() => exportReport('excel')} disabled={isExporting} className="bg-green-600 hover:bg-green-500 text-white">
            <Download className="w-4 h-4 mr-2" />Excel Completo
          </Button>
          <Button onClick={() => exportReport('professionals')} disabled={isExporting} variant="outline" className="border-[#2a2a2a] text-gray-300">
            <Download className="w-4 h-4 mr-2" />CSV Profesionales
          </Button>
          <Button onClick={() => exportReport('memberships')} disabled={isExporting} variant="outline" className="border-[#2a2a2a] text-gray-300">
            <Download className="w-4 h-4 mr-2" />CSV Membresías
          </Button>
          <Button onClick={() => exportReport('report')} disabled={isExporting} className="bg-white text-black hover:bg-gray-200">
            <Download className="w-4 h-4 mr-2" />Reporte PDF
          </Button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard
          icon={Users}
          label="Profesionales Activos"
          value={overview.active_professionals || 0}
          color="bg-green-500/10 text-green-400"
          link="/admin/profesionales"
        />
        <StatCard
          icon={Clock}
          label="Pendientes de Aprobación"
          value={overview.pending_professionals || 0}
          color="bg-amber-500/10 text-amber-400"
          link="/admin/profesionales?status=Pendiente"
        />
        <StatCard
          icon={FileText}
          label="Documentos por Revisar"
          value={stats?.pending_documents || 0}
          color="bg-blue-500/10 text-blue-400"
          link="/admin/documentos"
        />
        <StatCard
          icon={CreditCard}
          label="Membresías Vigentes"
          value={overview.active_memberships || 0}
          color="bg-purple-500/10 text-purple-400"
          link="/admin/membresias"
        />
      </div>

      {/* Revenue & Hours */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-gradient-to-br from-amber-500/20 to-amber-600/10 border border-amber-500/20 rounded-xl p-6">
          <div className="flex items-center gap-3 mb-2">
            <Activity className="w-6 h-6 text-amber-400" />
            <span className="text-gray-400">Ingresos Totales</span>
          </div>
          <p className="text-3xl font-manrope font-bold text-white">${(overview.total_revenue || 0).toLocaleString('es-MX')} <span className="text-lg text-gray-400">MXN</span></p>
        </div>
        <div className="bg-[#111111] border border-[#2a2a2a] rounded-xl p-6">
          <div className="flex items-center gap-3 mb-2">
            <GraduationCap className="w-6 h-6 text-blue-400" />
            <span className="text-gray-400">Horas Verificadas</span>
          </div>
          <p className="text-3xl font-manrope font-bold text-white">{overview.total_verified_hours || 0} <span className="text-lg text-gray-400">hrs</span></p>
        </div>
        <div className="bg-[#111111] border border-[#2a2a2a] rounded-xl p-6">
          <div className="flex items-center gap-3 mb-2">
            <BarChart3 className="w-6 h-6 text-green-400" />
            <span className="text-gray-400">Programas Publicados</span>
          </div>
          <p className="text-3xl font-manrope font-bold text-white">{overview.published_programs || 0} <span className="text-lg text-gray-400">/ {overview.total_programs || 0}</span></p>
        </div>
      </div>

      {/* Charts Grid */}
      <div className="grid lg:grid-cols-2 gap-8 mb-8">
        <SimplePieChart data={charts.professionals_by_status} title="Profesionales por Estatus" />
        <SimplePieChart data={charts.memberships_by_status} title="Membresías por Estatus" />
      </div>

      <div className="grid lg:grid-cols-2 gap-8 mb-8">
        <SimpleBarChart data={charts.by_rank} title="Distribución por Rango" />
        <SimpleBarChart data={charts.by_state?.slice(0, 8)} title="Top Estados" />
      </div>

      <TrendChart data={charts.monthly_trends} title="Tendencias Mensuales" />
    </div>
  );
}
